import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Typography,
  InputAdornment,
  Divider,
  IconButton,
  useMediaQuery,
  Box,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { IonAlert } from "@ionic/react";
import AsignacionesV2Service from "../services/asignacionesV2";

interface Props {
  open: boolean;
  psId: string | number;
  onClose: () => void;
  onGuardado: () => void;
}

const DIAS_FORM = [
  { key: "lun", label: "Lunes", ent: "lun_ent", sal: "lun_sal" },
  { key: "mar", label: "Martes", ent: "mar_ent", sal: "mar_sal" },
  { key: "mie", label: "Miércoles", ent: "mie_ent", sal: "mie_sal" },
  { key: "jue", label: "Jueves", ent: "jue_ent", sal: "jue_sal" },
  { key: "vie", label: "Viernes", ent: "vie_ent", sal: "vie_sal" },
  { key: "sab", label: "Sábado", ent: "sab_ent", sal: "sab_sal" },
  { key: "dom", label: "Domingo", ent: "dom_ent", sal: "dom_sal" },
];

type HorarioDia = { activo: boolean; ent: string; sal: string };
type Corte = { horarios: Record<string, HorarioDia> };

const corteVacio = (): Corte => ({
  horarios: DIAS_FORM.reduce((acc, d) => {
    acc[d.key] = { activo: false, ent: "", sal: "" };
    return acc;
  }, {} as Record<string, HorarioDia>),
});

const formatMiles = (val: any) => {
  if (val === "" || val === null || val === undefined) return "";
  const num = Math.floor(Number(val));
  if (!Number.isFinite(num) || num < 0) return "";
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const ModalAgregarDetalle: React.FC<Props> = ({ open, psId, onClose, onGuardado }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [genero, setGenero] = useState("I");
  const [perfil, setPerfil] = useState("");
  const [perfilesDisponibles, setPerfilesDisponibles] = useState<any[]>([]);
  const [supervisor, setSupervisor] = useState(false);
  const [cortes, setCortes] = useState<Corte[]>([corteVacio()]);

  // Viaticos
  const [viaticoGourmet, setViaticoGourmet] = useState(false);
  const [viaticoOperario, setViaticoOperario] = useState(false);
  const [montoGourmet, setMontoGourmet] = useState("");
  const [montoOperario, setMontoOperario] = useState("");
  const [gourmetDisponible, setGourmetDisponible] = useState("0");
  const [viaticoDisponible, setViaticoDisponible] = useState("0");

  // Candidatos
  const [candidatos, setCandidatos] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccionado, setSeleccionado] = useState("");
  const [filtroCand, setFiltroCand] = useState("");
  const [noDisponibles, setNoDisponibles] = useState<any[]>([]);
  const [buscandoMotivo, setBuscandoMotivo] = useState(false);
  // Resultado de la busqueda puntual (operarios_disponibles_v3) y control de "fuera de optimo".
  const [resultadoBusqueda, setResultadoBusqueda] = useState<any[]>([]);
  const [fueraOptimo, setFueraOptimo] = useState(false);
  const [confirmadoFueraOptimo, setConfirmadoFueraOptimo] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [alertMsg, setAlertMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    setFechaInicio("");
    setFechaFin("");
    setGenero("I");
    setPerfil("");
    setSupervisor(false);
    setCortes([corteVacio()]);
    setViaticoGourmet(false);
    setViaticoOperario(false);
    setMontoGourmet("");
    setMontoOperario("");
    setCandidatos([]);
    setSeleccionado("");
    setFiltroCand("");
    setNoDisponibles([]);
    setResultadoBusqueda([]);
    setFueraOptimo(false);
    setConfirmadoFueraOptimo(false);
    setConfirmOpen(false);
    (async () => {
      const disp = await AsignacionesV2Service.beneficios(psId);
      setGourmetDisponible(disp?.gourmet_disponible || "0");
      setViaticoDisponible(disp?.viatico_disponible || "0");
    })();
    (async () => {
      const lista = await AsignacionesV2Service.perfiles();
      setPerfilesDisponibles(lista || []);
      // Por defecto: "Operario en Formación" (id 1). El SP espera el ID del perfil.
      const porDefecto = (lista || []).find((p: any) => Number(p.id) === 1);
      if (porDefecto) setPerfil(String(porDefecto.id));
    })();
  }, [open, psId]);

  const setDia = (corteIdx: number, dayKey: string, campo: keyof HorarioDia, valor: any) => {
    setCortes((prev) =>
      prev.map((c, i) =>
        i !== corteIdx ? c : { horarios: { ...c.horarios, [dayKey]: { ...c.horarios[dayKey], [campo]: valor } } }
      )
    );
  };

  const agregarCorte = () => setCortes((prev) => [...prev, corteVacio()]);
  const quitarCorte = (idx: number) => setCortes((prev) => prev.filter((_, i) => i !== idx));

  // Campos {lun_ent, lun_sal, ...} de un corte (solo dias activos y completos).
  const camposHorarioCorte = (corte: Corte) => {
    const campos: Record<string, string> = {};
    for (const d of DIAS_FORM) {
      const h = corte.horarios[d.key];
      if (h.activo && h.ent && h.sal) {
        campos[d.ent] = h.ent;
        campos[d.sal] = h.sal;
      }
    }
    return campos;
  };

  const cortesValidos = () => cortes.map(camposHorarioCorte).filter((c) => Object.keys(c).length > 0);

  const validarHorario = () => {
    if (!fechaInicio) {
      setAlertMsg("Indique la fecha de inicio.");
      return false;
    }
    if (cortesValidos().length === 0) {
      setAlertMsg("Cargue al menos un día con entrada y salida.");
      return false;
    }
    return true;
  };

  // Ejecuta el SP de operarios disponibles con el horario/perfil/fecha actuales.
  const ejecutarConsulta = async (): Promise<any[]> => {
    const validos = cortesValidos();
    const filtros = {
      ps_id: psId,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      perfil,
      genero,
      supervisor,
      ...(validos[0] || {}),
    };
    const res = await AsignacionesV2Service.buscarCandidatos(filtros);
    setCandidatos(res || []);
    setNoDisponibles([]);
    return res || [];
  };

  // Consulta TODOS los operarios disponibles (ranking IA).
  const handleBuscar = async () => {
    if (!validarHorario()) return;
    setBuscando(true);
    const res = await ejecutarConsulta();
    setBuscando(false);
    if (res.length === 0) {
      setAlertMsg("No se encontraron operarios disponibles para ese horario.");
    }
  };

  // ¿El candidato esta en la lista de optimos (operarios_disponibles_test3 + IA)?
  const esOptimo = (cand: any) => {
    const leg = String(cand.nroLegajo || cand.legajo || "");
    return candidatos.some((c) => String(c.nroLegajo || c.legajo || "") === leg);
  };

  // Busca un operario especifico por su legajo/nombre ejecutando operarios_disponibles_v3.
  // Si pasa los filtros de v3 -> seleccionable (puede quedar fuera de optimos); si no -> motivo.
  const buscarEspecifico = async () => {
    const q = filtroCand.trim();
    if (!q) {
      setAlertMsg("Escriba el legajo o nombre del operario a buscar.");
      return;
    }
    if (!validarHorario()) return;
    const validos = cortesValidos();
    setBuscando(true);
    setNoDisponibles([]);
    setResultadoBusqueda([]);
    const lista = await AsignacionesV2Service.buscarCandidatos({
      ps_id: psId,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      perfil,
      sp: "operarios_disponibles_v3",
      ...(validos[0] || {}),
    });
    setBuscando(false);
    const matches = (lista || []).filter((c) => {
      const txt = `${c.nombres || c.nombre || ""} ${c.nroLegajo || c.legajo || ""}`.toLowerCase();
      return txt.includes(q.toLowerCase());
    });
    if (matches.length === 0) {
      // No paso los filtros de v3: mostramos el motivo.
      await verMotivo();
      return;
    }
    setResultadoBusqueda(matches);
    if (matches.length === 1) seleccionarFueraOptimo(matches[0]);
  };

  const seleccionarCandidato = (cand: any) => {
    const legajo = cand.nroLegajo || cand.legajo || "";
    const nombre = cand.nombres || cand.nombre || cand.operario || "";
    setSeleccionado(`${nombre} - ${legajo}`);
    setFueraOptimo(false);
    setConfirmadoFueraOptimo(false);
  };

  // Selecciona un operario hallado por la busqueda v3 (marca fuera de optimo si corresponde).
  const seleccionarFueraOptimo = (cand: any) => {
    const legajo = cand.nroLegajo || cand.legajo || "";
    const nombre = cand.nombres || cand.nombre || cand.operario || "";
    setSeleccionado(`${nombre} - ${legajo}`);
    setFueraOptimo(!esOptimo(cand));
    setConfirmadoFueraOptimo(false);
  };

  // Consulta el motivo por el que un operario buscado (legajo/nombre) NO esta disponible.
  const verMotivo = async () => {
    const q = filtroCand.trim();
    if (!q) {
      setAlertMsg("Escriba el legajo o nombre del operario a consultar.");
      return;
    }
    const validos = cortesValidos();
    setBuscandoMotivo(true);
    const res = await AsignacionesV2Service.motivoOperario({
      q,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      ...(validos[0] || {}),
    });
    setNoDisponibles(res || []);
    setBuscandoMotivo(false);
    if (!res || res.length === 0) {
      setAlertMsg("No se encontro un operario con ese legajo/nombre.");
    }
  };

  // Candidatos disponibles filtrados client-side por el texto de busqueda.
  const candidatosFiltrados = filtroCand.trim()
    ? candidatos.filter((c) => {
        const txt = `${c.nombres || c.nombre || ""} ${c.nroLegajo || c.legajo || ""}`.toLowerCase();
        return txt.includes(filtroCand.trim().toLowerCase());
      })
    : candidatos;

  const parseMonto = (v: string) => (v ? parseFloat(String(v).replace(/\D/g, "")) : NaN);

  const handleGuardar = async (forzar = false) => {
    if (!seleccionado) {
      setAlertMsg("Seleccione un operario del ranking.");
      return;
    }
    if (!fechaInicio) {
      setAlertMsg("Indique la fecha de inicio.");
      return;
    }
    const validos = cortesValidos();
    if (validos.length === 0) {
      setAlertMsg("Cargue al menos un día con entrada y salida.");
      return;
    }

    // Si el operario no esta entre los candidatos optimos, pedir confirmacion.
    if (fueraOptimo && !forzar && !confirmadoFueraOptimo) {
      setConfirmOpen(true);
      return;
    }

    let mg: number | null = null;
    let mo: number | null = null;
    if (viaticoGourmet) {
      mg = parseMonto(montoGourmet);
      if (isNaN(mg) || mg <= 0) {
        setAlertMsg("Monto de Tarjeta Gourmet inválido.");
        return;
      }
      if (mg > (parseFloat(gourmetDisponible) || 0)) {
        setAlertMsg(`El monto de Tarjeta Gourmet supera el disponible (${formatMiles(gourmetDisponible)} ₲).`);
        return;
      }
    }
    if (viaticoOperario) {
      mo = parseMonto(montoOperario);
      if (isNaN(mo) || mo <= 0) {
        setAlertMsg("Monto de Viático Combustible/Pasaje inválido.");
        return;
      }
      if (mo > (parseFloat(viaticoDisponible) || 0)) {
        setAlertMsg(`El monto de Viático supera el disponible (${formatMiles(viaticoDisponible)} ₲).`);
        return;
      }
    }

    // Una sola asignacion con todos los cortes; el backend los guarda en una transaccion.
    // Replicamos el primer corte en el nivel superior para las validaciones legacy.
    const datos: any = {
      operario: seleccionado,
      fecha_inicio: fechaInicio,
      ps_id: psId,
      perfil_id: perfil,
      supervisor,
      confirmado_fuera_optimo: fueraOptimo ? 1 : 0,
      viatico_gourmet: viaticoGourmet,
      viatico_operario: viaticoOperario,
      cortes: validos,
      ...validos[0],
    };
    if (fechaFin) datos.fecha_fin = fechaFin;
    if (viaticoGourmet) datos.viatico_gourmet_monto = mg;
    if (viaticoOperario) datos.viatico_operario_monto = mo;

    try {
      const resp = await AsignacionesV2Service.guardar(datos);
      if (resp?.status === 200) {
        setAlertMsg(resp?.data?.mensaje || "Asignación creada correctamente.");
        onGuardado();
      } else {
        setAlertMsg(resp?.data?.error || "Error al crear la asignación.");
      }
    } catch (error: any) {
      setAlertMsg(error?.response?.data?.error || "Error al crear la asignación.");
    }
  };

  const tituloSeccion = (texto: string) => (
    <Typography variant="subtitle2" style={{ color: "#910434", fontWeight: "bold" }}>
      {texto}
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} scroll="paper" maxWidth="md" fullWidth fullScreen={fullScreen}>
      <IonAlert isOpen={!!alertMsg} onDidDismiss={() => setAlertMsg("")} message={alertMsg} buttons={["Cerrar"]} />
      <DialogTitle disableTypography style={{ background: "#910434" }}>
        <Typography variant="h6" style={{ color: "#ffffff", fontWeight: "bold" }}>
          Agregar Detalle de Asignación
        </Typography>
      </DialogTitle>
      <DialogContent dividers style={{ overflowX: "hidden" }}>
        {/* Datos generales */}
        {tituloSeccion("Datos Generales")}
        <Grid container spacing={1} style={{ marginTop: 2 }}>
          <Grid item xs={6} sm={3}>
            <TextField
              type="date"
              label="Fecha inicio"
              size="small"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              type="date"
              label="Fecha fin"
              size="small"
              fullWidth
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select label="Género" size="small" value={genero} onChange={(e) => setGenero(e.target.value)} fullWidth>
              <MenuItem value="I">Indiferente</MenuItem>
              <MenuItem value="M">Masculino</MenuItem>
              <MenuItem value="F">Femenino</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select label="Perfil" size="small" value={perfil} onChange={(e) => setPerfil(e.target.value)} fullWidth>
              <MenuItem value="">Todos</MenuItem>
              {perfilesDisponibles.map((p) => (
                <MenuItem key={p.id ?? p.especializacion} value={String(p.id)}>
                  {p.especializacion}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={supervisor} onChange={(e) => setSupervisor(e.target.checked)} />}
              label="Supervisor"
            />
          </Grid>
        </Grid>

        <Divider style={{ margin: "12px 0" }} />

        {/* Cortes de horario */}
        {cortes.map((corte, corteIdx) => (
          <div key={corteIdx} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {tituloSeccion(cortes.length > 1 ? `Horario por día — Corte ${corteIdx + 1}` : "Horario por día")}
              {cortes.length > 1 && (
                <IconButton size="small" title="Quitar corte" onClick={() => quitarCorte(corteIdx)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </div>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ whiteSpace: "nowrap" }}>Día</TableCell>
                  <TableCell align="center">Entrada</TableCell>
                  <TableCell align="center">Salida</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DIAS_FORM.map((d) => {
                  const h = corte.horarios[d.key];
                  return (
                    <TableRow key={d.key}>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        <FormControlLabel
                          style={{ marginRight: 0 }}
                          control={
                            <Checkbox size="small" checked={h.activo} onChange={(e) => setDia(corteIdx, d.key, "activo", e.target.checked)} />
                          }
                          label={d.label}
                        />
                      </TableCell>
                      <TableCell align="center" style={{ padding: "4px 2px" }}>
                        <TextField
                          type="time"
                          size="small"
                          disabled={!h.activo}
                          value={h.ent}
                          onChange={(e) => setDia(corteIdx, d.key, "ent", e.target.value)}
                          inputProps={{ step: 300, style: { padding: "6px 4px" } }}
                          style={{ width: 86 }}
                        />
                      </TableCell>
                      <TableCell align="center" style={{ padding: "4px 2px" }}>
                        <TextField
                          type="time"
                          size="small"
                          disabled={!h.activo}
                          value={h.sal}
                          onChange={(e) => setDia(corteIdx, d.key, "sal", e.target.value)}
                          inputProps={{ step: 300, style: { padding: "6px 4px" } }}
                          style={{ width: 86 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ))}
        <Button size="small" variant="outlined" onClick={agregarCorte} style={{ color: "#910434", borderColor: "#910434" }}>
          + Agregar corte
        </Button>

        <Divider style={{ margin: "12px 0" }} />

        {/* Viaticos */}
        {tituloSeccion("Beneficios")}
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={viaticoGourmet}
                  onChange={(e) => {
                    setViaticoGourmet(e.target.checked);
                    if (!e.target.checked) setMontoGourmet("");
                  }}
                />
              }
              label={`Tarjeta Gourmet (Disp: ${formatMiles(gourmetDisponible)} ₲)`}
            />
            {viaticoGourmet && (
              <TextField
                label="Monto Gourmet"
                size="small"
                value={formatMiles(montoGourmet)}
                onChange={(e) => setMontoGourmet(e.target.value.replace(/\D/g, ""))}
                InputProps={{ endAdornment: <InputAdornment position="end">₲</InputAdornment> }}
                fullWidth
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={viaticoOperario}
                  onChange={(e) => {
                    setViaticoOperario(e.target.checked);
                    if (!e.target.checked) setMontoOperario("");
                  }}
                />
              }
              label={`Viático Combustible/Pasaje (Disp: ${formatMiles(viaticoDisponible)} ₲)`}
            />
            {viaticoOperario && (
              <TextField
                label="Monto Viático"
                size="small"
                value={formatMiles(montoOperario)}
                onChange={(e) => setMontoOperario(e.target.value.replace(/\D/g, ""))}
                InputProps={{ endAdornment: <InputAdornment position="end">₲</InputAdornment> }}
                fullWidth
              />
            )}
          </Grid>
        </Grid>

        <Divider style={{ margin: "12px 0" }} />

        {/* Buscar candidatos */}
        <div style={{ textAlign: "center", margin: "8px 0" }}>
          <Button variant="contained" disabled={buscando} onClick={handleBuscar} style={{ background: "#910434", color: "white" }}>
            {buscando ? "CONSULTANDO..." : "CONSULTAR OPERARIOS DISPONIBLES"}
          </Button>
        </div>

        {candidatos.length > 0 && (
          <>
            {tituloSeccion("Operarios disponibles (ranking IA)")}
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Buscar operario (nombre o legajo)"
                value={filtroCand}
                onChange={(e) => setFiltroCand(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") buscarEspecifico();
                }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={buscando || buscandoMotivo}
                onClick={buscarEspecifico}
                style={{ background: "#910434", color: "white", whiteSpace: "nowrap" }}
              >
                {buscando || buscandoMotivo ? "..." : "Buscar"}
              </Button>
            </Box>
            <List dense>
              {candidatosFiltrados.map((cand, idx) => {
                const legajo = cand.nroLegajo || cand.legajo || "";
                const nombre = cand.nombres || cand.nombre || cand.operario || "";
                const etiqueta = `${nombre} - ${legajo}`;
                const sel = seleccionado === etiqueta;
                const dist = cand.distancia_km_dinamica;
                const detalle = [
                  cand.perfil ? `Perfil: ${cand.perfil}` : null,
                  dist != null ? `Dist: ${Number(dist).toFixed(1)} km` : null,
                  cand.horas_total_asignadas != null ? `Horas: ${cand.horas_total_asignadas}` : null,
                  cand.tiene_movilidad ? "Con movilidad" : null,
                  cand.justificacion ? `— ${cand.justificacion}` : null,
                ]
                  .filter(Boolean)
                  .join("  •  ");
                return (
                  <ListItem
                    key={`${legajo}-${idx}`}
                    button
                    selected={sel}
                    onClick={() => seleccionarCandidato(cand)}
                    style={sel ? { border: "2px solid #910434", borderRadius: 4 } : {}}
                  >
                    <ListItemText
                      primary={`${etiqueta}${cand.score_ia != null ? `   ·   IA: ${cand.score_ia}` : ""}`}
                      secondary={detalle}
                    />
                  </ListItem>
                );
              })}
              {candidatosFiltrados.length === 0 && (
                <Typography variant="caption" style={{ padding: 8, display: "block" }}>
                  Ningún operario disponible coincide con “{filtroCand}”. Presioná “Buscar” para ver el motivo.
                </Typography>
              )}
            </List>
          </>
        )}

        {noDisponibles.length > 0 && (
          <>
            {tituloSeccion("No disponibles (motivo)")}
            <List dense>
              {noDisponibles.map((o, idx) => (
                <ListItem key={`nd-${idx}`} style={{ borderLeft: "4px solid #c62828", background: "#fdecea", marginBottom: 4 }}>
                  <ListItemText
                    primary={`${o.nombres || ""} - ${o.nroLegajo || ""}`}
                    secondary={o.justificacion || o.motivo}
                    secondaryTypographyProps={{ style: { color: "#c62828" } }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {resultadoBusqueda.length > 0 && (
          <>
            {tituloSeccion("Resultado de búsqueda")}
            <List dense>
              {resultadoBusqueda.map((cand, idx) => {
                const legajo = cand.nroLegajo || cand.legajo || "";
                const nombre = cand.nombres || cand.nombre || cand.operario || "";
                const etiqueta = `${nombre} - ${legajo}`;
                const sel = seleccionado === etiqueta;
                return (
                  <ListItem
                    key={`rb-${legajo}-${idx}`}
                    button
                    selected={sel}
                    onClick={() => seleccionarFueraOptimo(cand)}
                    style={sel ? { border: "2px solid #ed6c02", borderRadius: 4 } : {}}
                  >
                    <ListItemText
                      primary={etiqueta}
                      secondary={[
                        cand.perfil ? `Perfil: ${cand.perfil}` : null,
                        cand.totalHoras != null ? `Horas: ${cand.totalHoras}` : null,
                        cand.ciudad ? `Ciudad: ${cand.ciudad}` : null,
                        esOptimo(cand) ? "Disponible" : "Disponible (no figura entre los óptimos)",
                      ]
                        .filter(Boolean)
                        .join("  •  ")}
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}

        {seleccionado && (
          <Typography variant="body2" style={{ marginTop: 8 }}>
            Operario seleccionado: <strong>{seleccionado}</strong>
            {fueraOptimo && <span style={{ color: "#ed6c02" }}> — fuera de candidatos óptimos</span>}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={() => handleGuardar()}
          variant="contained"
          disabled={!seleccionado}
          style={{ background: seleccionado ? "#910434" : "#cccccc", color: "white" }}
        >
          Agregar asignación
        </Button>
      </DialogActions>

      {/* Confirmacion: operario fuera de la lista de candidatos optimos */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle disableTypography style={{ background: "#ed6c02" }}>
          <Typography variant="h6" style={{ color: "#ffffff", fontWeight: "bold" }}>
            Operario fuera de óptimos
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            <strong>{seleccionado}</strong> no figura en la lista de candidatos óptimos, pero está
            disponible. ¿Desea asignarlo igualmente?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            style={{ background: "#ed6c02", color: "white" }}
            onClick={() => {
              setConfirmadoFueraOptimo(true);
              setConfirmOpen(false);
              handleGuardar(true);
            }}
          >
            Asignar igualmente
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ModalAgregarDetalle;
